import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { rupiah } from "@/lib/format";
import { api } from "@/lib/pos-api";
import { Banknote, ArrowLeftRight, QrCode, SplitSquareHorizontal, Check, HandCoins, UserPlus, User, CreditCard, Plus, X } from "lucide-react";
import { calculateSplitPayment, nextAutoPaymentIndex } from "@/lib/split-payment.mjs";
import { nextDirectionalIndex } from "@/lib/directional-navigation.mjs";

const ICONS = { tunai: Banknote, transfer: ArrowLeftRight, qris: QrCode, kartu_debit: CreditCard, kartu_kredit: CreditCard };

// Rekomendasi uang tunai: pembulatan ke atas mendekati total (bukan pecahan tetap 10rb/20rb).
function cashSuggestions(T) {
  if (T <= 0) return [];
  const set = new Set();
  for (const step of [1000, 5000, 10000, 50000, 100000]) {
    const v = Math.ceil(T / step) * step;
    if (v > T) set.add(v);
  }
  return [...set].sort((a, b) => a - b).slice(0, 4);
}

export default function PaymentModal({ open, onClose, total, onConfirm, customer, onPickCustomer, keyboardMode = false, settlementTotal = 0, busy = false, returnCredit = null }) {
  const [methods, setMethods] = useState([]);
  const [mode, setMode] = useState("tunai"); // method id | "split" | "piutang"
  const [cash, setCash] = useState("");
  const [dp, setDp] = useState("");
  const [lines, setLines] = useState([]);
  const [autoLine, setAutoLine] = useState(1);
  const [returnRemainder, setReturnRemainder] = useState("refund");
  const modalRef = useRef(null);
  const pendingSplitFocusRef = useRef(false);

  useEffect(() => {
    if (open) api.get("/payment-methods").then((r) => setMethods(r.data.filter((m) => m.active))).catch(() => setMethods([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      setMode("tunai");
      setCash("");
      setDp("");
      setReturnRemainder("refund");
    }
  }, [open]);

  const byId = useMemo(() => Object.fromEntries(methods.map((m) => [m.id, m])), [methods]);
  const feePct = (id) => { const m = byId[id]; return m && !m.is_cash ? Number(m.fee_percent || 0) : 0; };
  const returnCreditApplied = Math.min(Number(returnCredit?.amount || 0), Math.max(0, total - settlementTotal));
  const returnCreditRemainder = Math.max(0, Number(returnCredit?.amount || 0) - returnCreditApplied);
  const payableTotal = Math.max(0, total - returnCreditApplied);

  // Init split lines when entering split mode
  useEffect(() => {
    if (mode === "split") {
      const cashM = methods.find((m) => m.is_cash) || methods[0];
      const otherM = methods.find((m) => !m.is_cash) || methods[0];
      if (cashM && otherM) {
        setLines([{ method: cashM.id, amount: "" }, { method: otherM.id, amount: "" }]);
        setAutoLine(1);
      }
    }
  }, [mode]); // eslint-disable-line

  // ---- single method ----
  const singleFee = mode !== "split" && mode !== "piutang" ? Math.round(payableTotal * feePct(mode) / 100) : 0;
  const singleGrand = payableTotal + singleFee;
  const isCashMode = byId[mode]?.is_cash;
  const cashNum = Number(cash || 0);
  const change = isCashMode ? Math.max(0, cashNum - payableTotal) : 0;

  // ---- split ----
  const splitCalculation = calculateSplitPayment(lines, payableTotal, autoLine);
  const splitBasesFull = splitCalculation.amounts;
  const splitFee = lines.reduce((s, l, i) => s + Math.round(splitBasesFull[i] * feePct(l.method) / 100), 0);
  const splitGrand = payableTotal + splitFee;
  const splitSumOk = splitCalculation.valid;

  // ---- piutang ----
  const dpNum = Math.min(Number(dp || 0), payableTotal);
  const credit = Math.max(0, payableTotal - dpNum);

  let valid = false;
  if (payableTotal === 0) valid = true;
  else if (mode === "split") valid = splitSumOk && lines.every((l) => l.method);
  else if (mode === "piutang") valid = !!customer && dpNum >= settlementTotal;
  else if (isCashMode) valid = cashNum >= payableTotal;
  else valid = true;

  const confirm = () => {
    if (busy || !valid) return;
    let payload;
    if (payableTotal === 0) {
      payload = { payment_type: "return_credit", payments: [], paid: 0 };
    } else if (mode === "split") {
      payload = { payment_type: "split", payments: lines.map((l, i) => ({ method: l.method, amount: splitBasesFull[i] })), paid: splitGrand };
    } else if (mode === "piutang") {
      payload = { payment_type: "piutang", payments: dpNum > 0 ? [{ method: "tunai", amount: dpNum }] : [], paid: dpNum, customer_id: customer.id, customer_name: customer.name };
    } else if (isCashMode) {
      payload = { payment_type: mode, payments: [{ method: mode, amount: payableTotal }], paid: cashNum };
    } else {
      payload = { payment_type: mode, payments: [{ method: mode, amount: payableTotal }], paid: singleGrand };
    }
    if (customer && mode !== "piutang") { payload.customer_id = customer.id; payload.customer_name = customer.name; }
    if (returnCredit) {
      payload.return_credit_id = returnCredit.id;
      payload.return_credit_remainder = returnRemainder;
    }
    onConfirm(payload);
  };

  const addLine = () => { if (lines.length < 4) { const m = methods.find((x) => !x.is_cash) || methods[0]; setAutoLine(lines.length); setLines((p) => [...p, { method: m?.id, amount: "" }]); } };
  const removeLine = (i) => {
    if (lines.length <= 2) return;
    const nextLength = lines.length - 1;
    setAutoLine((current) => current === i ? nextLength - 1 : current > i ? current - 1 : current);
    setLines((previous) => previous.filter((_, index) => index !== i));
  };
  const setLine = (i, k, v) => setLines((p) => p.map((l, x) => (x === i ? { ...l, [k]: v } : l)));
  const setLineAmount = (i, value) => {
    setLines((previous) => previous.map((line, index) => (index === i ? { ...line, amount: value } : line)));
    setAutoLine(nextAutoPaymentIndex(lines.length, i));
  };

  const focusCashInput = () => {
    window.requestAnimationFrame(() => {
      const input = modalRef.current?.querySelector('[data-testid="cash-input"]');
      input?.focus();
      input?.select();
    });
  };

  const focusFirstSplitMethod = () => {
    pendingSplitFocusRef.current = true;
    window.requestAnimationFrame(() => {
      const select = modalRef.current?.querySelector('[data-testid="split-method-0"]');
      if (select) {
        select.focus();
        pendingSplitFocusRef.current = false;
      }
    });
  };

  useEffect(() => {
    if (mode !== "split" || lines.length === 0 || !pendingSplitFocusRef.current) return;
    window.requestAnimationFrame(() => {
      modalRef.current?.querySelector('[data-testid="split-method-0"]')?.focus();
      pendingSplitFocusRef.current = false;
    });
  }, [mode, lines.length]);

  const selectPaymentMethod = (paymentMethod) => {
    setMode(paymentMethod.key);
    if (paymentMethod.isCash) focusCashInput();
    if (paymentMethod.key === "split") focusFirstSplitMethod();
  };

  const tiles = (() => {
    const base = [
      ...methods.map((m) => ({ key: m.id, label: m.label, icon: ICONS[m.id] || CreditCard, isCash: m.is_cash, fee: !m.is_cash && m.fee_percent > 0 ? m.fee_percent : 0 })),
      { key: "split", label: "Split", icon: SplitSquareHorizontal },
      { key: "piutang", label: "Piutang", icon: HandCoins },
    ];
    const used = new Set();
    return base.map((t) => {
      let sc = null;
      for (const ch of (t.label || "").toUpperCase()) {
        if (/[A-Z0-9]/.test(ch) && !used.has(ch)) { sc = ch; break; }
      }
      if (!sc) for (const d of "123456789") if (!used.has(d)) { sc = d; break; }
      if (sc) used.add(sc);
      return { ...t, sc };
    });
  })();

  const onKeyDown = (e) => {
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
      const isSelect = e.target.tagName === "SELECT";
      if (!(isSelect && ["ArrowUp", "ArrowDown"].includes(e.key))) {
        const selectedTile = e.target.closest?.("[data-pay-method-key]");
        if (selectedTile && e.key === "ArrowDown") {
          const primary = modalRef.current?.querySelector('[data-pay-primary="true"]');
          if (primary) { e.preventDefault(); primary.focus(); return; }
        }
        const controls = [...(modalRef.current?.querySelectorAll('[data-pay-nav="true"]:not(:disabled)') || [])]
          .filter((element) => element.getClientRects().length > 0);
        const currentIndex = controls.indexOf(e.target);
        if (currentIndex >= 0) {
          const direction = e.key.replace("Arrow", "").toLowerCase();
          const nextIndex = nextDirectionalIndex(controls.map((element) => element.getBoundingClientRect()), currentIndex, direction);
          if (nextIndex >= 0) {
            e.preventDefault();
            const nextControl = controls[nextIndex];
            nextControl.focus();
            if (selectedTile && nextControl.dataset.payMethodKey) setMode(nextControl.dataset.payMethodKey);
            return;
          }
        }
      }
    }
    if (e.key === "Enter") {
      if (e.target.tagName === "SELECT") return;
      const methodTile = e.target.closest?.("[data-pay-method-key]");
      if (methodTile) {
        const paymentMethod = tiles.find((tile) => tile.key === methodTile.dataset.payMethodKey);
        if (paymentMethod) {
          e.preventDefault();
          selectPaymentMethod(paymentMethod);
        }
        return;
      }
      if (valid) { e.preventDefault(); confirm(); }
      return;
    }
    if (!keyboardMode) return;
    const k = (e.key || "").toUpperCase();
    const t = tiles.find((x) => x.sc === k);
    if (t) {
      e.preventDefault();
      selectPaymentMethod(t);
      if (!t.isCash && t.key !== "split") window.requestAnimationFrame(() => modalRef.current?.querySelector(`[data-pay-method-key="${t.key}"]`)?.focus());
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent ref={modalRef} data-testid="payment-modal" onKeyDown={onKeyDown} className="bg-ui-card border-ui-line text-ui-text max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Pembayaran</DialogTitle>
          <DialogDescription className="text-ui-muted text-xs">Pilih metode pembayaran dan selesaikan transaksi.</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl bg-ui-card border border-ui-line p-4 space-y-2">
          <div className="flex items-center justify-between"><span className="text-ui-muted">Total Belanja</span><span className="font-mono font-bold text-xl">{rupiah(total)}</span></div>
          {returnCredit && <div className="flex items-center justify-between text-brand-readable"><span>Saldo Retur</span><span className="font-mono font-bold">-{rupiah(returnCreditApplied)}</span></div>}
          <div className="border-t border-ui-line pt-2 flex items-center justify-between"><span className="font-semibold">Harus Dibayar</span><span className="font-mono font-extrabold text-3xl text-brand-readable">{rupiah(payableTotal)}</span></div>
        </div>

        {returnCreditRemainder > 0 && <div className="rounded-xl border border-warning/40 bg-warning-soft p-3 space-y-2 text-sm">
          <div className="flex justify-between font-semibold"><span>Sisa saldo retur</span><span className="font-mono">{rupiah(returnCreditRemainder)}</span></div>
          <select value={returnRemainder} onChange={(event) => setReturnRemainder(event.target.value)} className="h-10 w-full rounded-md bg-ui-card border border-ui-line px-3">
            <option value="refund">Kembalikan Tunai</option>
            {returnCredit?.customer_id && <option value="customer_balance">Jadikan saldo pelanggan</option>}
          </select>
        </div>}

        <button data-testid="payment-customer-btn" onClick={onPickCustomer} className="w-full flex items-center gap-2 rounded-xl border border-ui-line bg-ui-soft px-3 py-2.5 text-left hover:border-brand/60 no-select">
          {customer ? <User className="h-4 w-4 text-brand-readable" /> : <UserPlus className="h-4 w-4 text-ui-muted" />}
          <span className="text-sm flex-1 truncate">
            {customer ? <span className="text-ui-text font-semibold">{customer.name}</span> : <span className="text-ui-muted">Pilih pelanggan (F3) — wajib untuk Piutang</span>}
          </span>
          <span className="text-[11px] text-brand-readable">Ubah</span>
        </button>

        {payableTotal > 0 && <div className="grid grid-cols-4 gap-2">
          {tiles.map((m) => (
            <button key={m.key} data-pay-nav="true" data-pay-method-key={m.key} data-testid={`pay-method-${m.key}`} onClick={() => selectPaymentMethod(m)}
              className={`relative flex flex-col items-center gap-1 py-3 rounded-xl border transition-colors no-select ${mode === m.key ? "border-brand bg-brand-soft text-brand-readable" : "border-ui-line bg-ui-soft text-ui-muted hover:text-ui-text"}`}>
              <m.icon className="h-5 w-5" />
              <span className="text-[11px] font-semibold text-center leading-tight">{m.label}</span>
              {keyboardMode && m.sc ? <span className="absolute top-1 left-1 text-[9px] font-bold text-ui-muted bg-ui-soft/80 rounded px-1 border border-ui-line">{m.sc}</span> : null}
              {m.fee ? <span className="absolute top-1 right-1 text-[9px] font-bold text-warning-readable">+{m.fee}%</span> : null}
            </button>
          ))}
        </div>}

        {/* Single cash */}
        {payableTotal > 0 && mode !== "split" && mode !== "piutang" && isCashMode && (
          <div className="space-y-3">
            <MoneyInput data-pay-nav="true" data-pay-primary="true" data-testid="cash-input" value={cash} onValueChange={setCash} onFocus={(event) => event.target.select()} placeholder="Uang diterima" className="h-14 text-right font-mono text-2xl bg-ui-card border-ui-line" autoFocus />
            <div className="grid grid-cols-4 gap-2">
              {cashSuggestions(payableTotal).map((q) => (
                <button key={q} data-pay-nav="true" data-testid={`quick-cash-${q}`} onClick={() => setCash(String(q))} className="h-11 rounded-lg bg-ui-card border border-ui-line text-sm font-mono font-semibold hover:border-brand no-select">
                  {rupiah(q).replace("Rp", "")}
                </button>
              ))}
              <button data-pay-nav="true" data-testid="quick-cash-pas" onClick={() => setCash(String(payableTotal))} className="h-11 rounded-lg bg-brand-soft border border-brand/50 text-brand-readable text-sm font-bold hover:bg-brand-soft no-select">Pas</button>
            </div>
            <div className="flex items-center justify-between px-1">
              <span className="text-ui-muted">Kembalian</span>
              <span data-testid="change-amount" className="font-mono font-bold text-xl text-warning-readable">{rupiah(change)}</span>
            </div>
          </div>
        )}

        {/* Single non-cash */}
        {payableTotal > 0 && mode !== "split" && mode !== "piutang" && !isCashMode && (
          <div className="space-y-2 py-2">
            {singleFee > 0 && (
              <>
                <div className="flex justify-between text-sm text-ui-muted"><span>Biaya {byId[mode]?.label} ({feePct(mode)}%)</span><span className="font-mono text-warning-readable">+{rupiah(singleFee)}</span></div>
                <div className="rounded-xl bg-ui-card border border-brand/40 p-3 flex items-center justify-between">
                  <span className="text-ui-muted font-semibold">Total Bayar</span>
                  <span data-testid="single-grand" className="font-mono font-bold text-2xl text-brand-readable">{rupiah(singleGrand)}</span>
                </div>
              </>
            )}
            <p className="text-ui-muted text-sm text-center">Konfirmasi setelah pembayaran {byId[mode]?.label} diterima.</p>
          </div>
        )}

        {/* Split */}
        {payableTotal > 0 && mode === "split" && (
          <div className="space-y-2">
            {lines.map((l, i) => {
              const isAuto = i === autoLine;
              const base = splitBasesFull[i];
              const f = Math.round(base * feePct(l.method) / 100);
              return (
                <div key={i} data-testid={`split-line-${i}`} className="flex items-center gap-2">
                  <select data-pay-nav="true" data-pay-primary={i === 0 ? "true" : undefined} data-testid={`split-method-${i}`} value={l.method} onChange={(e) => setLine(i, "method", e.target.value)} className="h-11 rounded-lg bg-ui-card border border-ui-line text-sm px-2 w-36">
                    {methods.map((m) => <option key={m.id} value={m.id}>{m.label}{!m.is_cash && m.fee_percent > 0 ? ` +${m.fee_percent}%` : ""}</option>)}
                  </select>
                  <div className="flex-1 min-w-0">
                    <MoneyInput data-pay-nav="true" data-testid={`split-amount-${i}`} value={isAuto ? base : l.amount} onValueChange={(value) => setLineAmount(i, value)} onFocus={(event) => event.target.select()} placeholder="0" aria-label={`${byId[l.method]?.label || "Metode"}${isAuto ? " sisa otomatis" : " manual"}`} className={`w-full h-11 text-right font-mono border-ui-line ${isAuto ? "bg-brand-soft text-brand-readable font-bold" : "bg-ui-card"}`} />
                    <div className={`mt-0.5 text-[9px] leading-none text-right ${isAuto ? "text-brand-readable font-semibold" : "text-ui-muted"}`}>{isAuto ? "Sisa otomatis" : "Isi manual"}</div>
                  </div>
                  {f > 0 ? <span className="text-[10px] text-warning-readable w-16 text-right">+{rupiah(f)}</span> : <span className="w-16" />}
                  {lines.length > 2 ? <button data-testid={`split-remove-${i}`} onClick={() => removeLine(i)} className="text-ui-muted hover:text-danger-readable"><X className="h-4 w-4" /></button> : <span className="w-4" />}
                </div>
              );
            })}
            {lines.length < 4 && <button data-pay-nav="true" data-testid="split-add-line" onClick={addLine} className="text-xs text-brand-readable flex items-center gap-1 hover:text-brand-readable"><Plus className="h-3.5 w-3.5" /> Tambah metode</button>}
            <div className="rounded-xl bg-ui-card border border-ui-line p-3 space-y-1">
              {splitFee > 0 && <div className="flex justify-between text-sm text-ui-muted"><span>Total Biaya</span><span className="font-mono text-warning-readable">+{rupiah(splitFee)}</span></div>}
              <div className="flex justify-between"><span className="text-ui-muted font-semibold">Total Bayar</span><span data-testid="split-grand" className="font-mono font-bold text-xl text-brand-readable">{rupiah(splitGrand)}</span></div>
            </div>
            {!splitSumOk && <p className="text-[11px] text-warning-readable text-center">Nominal metode melebihi total tagihan.</p>}
          </div>
        )}

        {/* Piutang */}
        {payableTotal > 0 && mode === "piutang" && (
          <div className="space-y-3">
            {!customer && <div data-testid="piutang-need-customer" className="text-xs text-warning-readable bg-warning-soft border border-warning/30 rounded-lg px-3 py-2">Pilih pelanggan dulu (F3) untuk mencatat piutang.</div>}
            {settlementTotal > 0 && <p data-testid="settlement-dp-notice" className="text-xs text-warning-readable">DP minimal {rupiah(settlementTotal)} untuk melunasi piutang sebelumnya. Sisa belanja dapat dicatat sebagai piutang baru.</p>}
            <div>
              <label className="text-xs text-ui-muted uppercase tracking-wide">Uang Muka / DP (opsional)</label>
              <MoneyInput data-pay-nav="true" data-pay-primary="true" data-testid="piutang-dp-input" value={dp} onValueChange={setDp} onFocus={(event) => event.target.select()} placeholder="0" className="h-12 text-right font-mono text-lg bg-ui-card border-ui-line mt-1" />
            </div>
            <div className="rounded-xl bg-ui-card border border-danger/40 p-3 flex items-center justify-between">
              <span className="text-ui-muted flex items-center gap-2"><HandCoins className="h-4 w-4 text-danger-readable" /> Sisa Piutang</span>
              <span data-testid="piutang-credit-amount" className="font-mono font-bold text-lg text-danger-readable">{rupiah(credit)}</span>
            </div>
          </div>
        )}

        <Button data-pay-nav="true" data-pay-primary={mode !== "split" && mode !== "piutang" && !isCashMode ? "true" : undefined} data-testid="confirm-payment-btn" disabled={!valid || busy} onClick={confirm} className="w-full h-14 text-lg font-bold bg-brand-button hover:bg-brand-button text-ui-text gap-2">
          <Check className="h-5 w-5" /> Selesaikan Pembayaran (Enter)
        </Button>
      </DialogContent>
    </Dialog>
  );
}
